import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { AlertCircle, XClose } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import {
	Tab as DrawerTab,
	TabList,
	Tabs,
} from "@/components/application/tabs/tabs";
import { BadgeGroup } from "@/components/base/badges/badge-groups";
import { Button } from "@/components/base/buttons/button";
import { NativeSelect } from "@/components/base/select/select-native";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { DrawerHeader } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { Illustration } from "@/components/shared-assets/illustrations";
import { DrawerProvider, useDrawerStore } from "@/contexts/drawer-context";
import { useEditOrgAdminUserDetailsForm } from "@/forms/(org-admin)/user/edit-org-user-details/edit-org-user-details.form.hooks";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import { organizationQueryOptions } from "@/services/organization";
import { getUserInitials } from "@/utils/get-user-initials";

export const ModeSchema = z.enum(["edit", "view"]);
export const TabSchema = z.enum(["details", "sub-organizations", "activity"]);

export const SearchSchema = z.object({
	mode: ModeSchema.optional().default("view"),
	tab: TabSchema.optional(),
});

export type Mode = z.infer<typeof ModeSchema>;
export type Tab = z.infer<typeof TabSchema>;
export type Search = z.infer<typeof SearchSchema>;

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users/(org-users)/$orgUserId",
)({
	component: RouteComponent,
	params: {
		parse: (params) => ({
			orgUserId: z.string().parse(params.orgUserId),
		}),
	},
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

export const statusColors = {
	Pending: "blue",
	Active: "success",
	Suspended: "error",
	Archived: "gray",
	Removed: "gray",
} as const;

function RouteComponent() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users/(org-users)/$orgUserId",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users/(org-users)/$orgUserId",
	});
	const membershipEdit = useEntityActionAllowed({
		name: "UpdateOrganizationUserInfo",
		entityType: "OrganizationUser",
		entityId: params.orgUserId,
	});
	const canEditMembership = membershipEdit.allowed && !membershipEdit.isPending;
	const [isOpen, setIsOpen] = useState(false);
	const [showOverlay, setShowOverlay] = useState(false);
	const [isOverlayAnimating, setIsOverlayAnimating] = useState(false);

	// Extract current tab from the pathname
	const currentTab = (location.pathname.split("/").pop() as Tab) || "details";

	const userById = useQuery(
		organizationQueryOptions.users.detail(params.orgId, params.orgUserId),
	);

	// Open drawer when component mounts (route is accessed)
	useEffect(() => {
		setIsOpen(true);
	}, []);

	// Handle overlay animation based on mode changes
	useEffect(() => {
		if (
			search.mode === "edit" &&
			canEditMembership &&
			isOpen &&
			currentTab !== "details"
		) {
			// Fade in overlay when entering edit mode
			setShowOverlay(true);
			setIsOverlayAnimating(true);
			// Remove animation class after animation completes
			const timer = setTimeout(() => {
				setIsOverlayAnimating(false);
			}, 300);
			return () => clearTimeout(timer);
		} else if (search.mode === "view" && showOverlay) {
			// Fade out overlay when exiting edit mode
			setIsOverlayAnimating(true);
			// const timer = setTimeout(() => {
			setShowOverlay(false);
			setIsOverlayAnimating(false);
			// }, 300);
			// return () => clearTimeout(timer);
		}
	}, [canEditMembership, currentTab, search.mode, isOpen, showOverlay]);

	// Handle drawer close - navigate back to parent route
	const handleClose = () => {
		setIsOpen(false);
		// Delay navigation to allow exit animation to complete (500ms)
		setTimeout(() => {
			navigate({
				to: `/org-admin/$orgId/users`,
				params: { orgId: params.orgId },
				replace: true,
			});
		}, 200);
	};

	// Handle drawer state change
	const handleOpenChange = (open: boolean) => {
		// Prevent closing when in edit mode (persistent)
		if (
			!open &&
			search.mode === "edit" &&
			canEditMembership &&
			currentTab !== "details"
		) {
			return; // Don't close the drawer
		}

		setIsOpen(open);
		if (!open) {
			// If drawer is being closed, navigate back
			handleClose();
		}
	};

	// Determine drawer title and content based on mode
	const title = userById.data?.displayName
		? userById.data?.displayName
		: `${userById.data?.firstName || "User"} ${userById.data?.lastName || ""}`;

	// Get user avatar and initials
	const { src: userAvatarSrc, isLoading: isAvatarLoading } = useUserAvatarSrc(
		userById.data?.userId || "",
		userById.data?.hasAvatar,
	);
	const userInitials = getUserInitials(
		userById.data?.firstName,
		userById.data?.lastName,
	);
	const tabs = [
		{
			label: t("org-admin.users.tabs.details"),
			id: "details",
		},
		{
			label: `${t("subOrganization.entityLabel")}s`,
			id: "sub-organizations",
		},
		{
			label: t("org-admin.users.tabs.activity"),
			id: "activity",
		},
	];

	// Get the current tab info and create edit mode tabs
	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [{ label: `Edit ${currentTabInfo.label}`, id: currentTabInfo.id }]
		: [];

	useEffect(() => {
		if (
			currentTab !== "details" ||
			search.mode !== "edit" ||
			!userById.data?.userId
		) {
			return;
		}
		navigate({
			to: `/org-admin/$orgId/users/$orgUserId/$userId/details`,
			params: {
				orgId: params.orgId,
				orgUserId: params.orgUserId,
				userId: userById.data.userId,
			},
			search: { mode: "view" },
			replace: true,
		});
	}, [
		currentTab,
		navigate,
		params.orgId,
		params.orgUserId,
		search.mode,
		userById.data?.userId,
	]);

	useEffect(() => {
		if (
			currentTab === "details" ||
			search.mode !== "edit" ||
			membershipEdit.isPending ||
			membershipEdit.allowed ||
			!userById.data?.userId
		) {
			return;
		}
		navigate({
			to: `/org-admin/$orgId/users/$orgUserId/$userId/${currentTab}`,
			params: {
				orgId: params.orgId,
				orgUserId: params.orgUserId,
				userId: userById.data?.userId ?? "",
			},
			search: { mode: "view" },
			replace: true,
		});
	}, [
		currentTab,
		membershipEdit.allowed,
		membershipEdit.isPending,
		navigate,
		params.orgId,
		params.orgUserId,
		search.mode,
		userById.data?.userId,
	]);

	const editOrgAdminUserDetailsForm = useEditOrgAdminUserDetailsForm(
		params.orgId,
		params.orgUserId,
		() => {
			// Success callback - navigate back to view mode to show success message
			navigate({
				to: "/org-admin/$orgId/users/$orgUserId/$userId/details",
				params: {
					orgId: params.orgId,
					orgUserId: params.orgUserId,
					userId: userById.data?.userId ?? "",
				},
				search: { mode: "view" },
			});
		},
	);

	const hideTabs = false;
	const showEditChrome =
		search.mode === "edit" && canEditMembership && currentTab !== "details";

	return (
		<DrawerProvider>
			{/* Custom overlay for edit mode */}
			{showOverlay && (
				<div
					className={`fixed inset-0 bg-overlay/70 z-30 transition-opacity duration-100 ${
						search.mode === "edit" && isOverlayAnimating
							? "animate-in fade-in"
							: search.mode === "view" && isOverlayAnimating
								? "animate-out fade-out"
								: "opacity-100"
					}`}
					role="presentation"
					aria-hidden="true"
					onClick={(e) => {
						// Prevent closing when clicking overlay in edit mode
						e.preventDefault();
						e.stopPropagation();
					}}
					onKeyDown={(e) => {
						// Prevent closing when pressing keys on overlay in edit mode
						e.preventDefault();
						e.stopPropagation();
					}}
				/>
			)}
			<Drawer
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				width="40%"
				backdropPreset="transparent"
				dialogClassName="gap-0 overflow-hidden"
			>
				{({ close }) => (
					<>
						<editOrgAdminUserDetailsForm.AppForm>
							<DrawerHeader
								type="default"
								showSpacer={false}
								title={title}
								avatar={{
									src: userAvatarSrc || undefined,
									alt: title,
									initials:
										!isAvatarLoading && !userAvatarSrc
											? userInitials
											: undefined,
									size: "lg",
								}}
								metadata={[
									{
										label: t("org-admin.users.drawer.description", {
											subOrganization: t(
												"subOrganization.entityLabel",
											).toLowerCase(),
										}),
									},
								]}
								iconActions={
									showEditChrome
										? []
										: [
												{
													label: t("common.actions.close"),
													icon: XClose,
													onPress: () => {
														close();
														handleClose();
													},
												},
											]
								}
							/>
							<Drawer.ScrollBody showPlaceholder={false}>
								{userById.isLoading ? (
									<div className="flex w-full flex-1 items-center justify-center">
										<LoadingIndicator
											type="line-simple"
											size="xl"
											supportingText
										/>
									</div>
								) : userById.isError ? (
									<div className="flex flex-col items-center justify-center h-full gap-4">
										<Illustration type="cloud" size="sm">
											<div className="bg-error-500 rounded-full p-3">
												<AlertCircle className="size-6" />
											</div>
										</Illustration>
										<div className="flex flex-col items-center justify-center pt-4">
											<p className="text-gray-900 font-semibold text-md">
												{t("org-admin.users.errors.errorLoadingPage")}
											</p>
											<div className="w-[60%]">
												<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
													{t("org-admin.users.errors.errorLoadingContent")}
												</p>
											</div>
										</div>
									</div>
								) : (
									<div className="flex w-full flex-col">
										<NativeSelect
											aria-label="Tabs"
											value={currentTab}
											onChange={(event) =>
												navigate({
													to: `/org-admin/$orgId/users/$orgUserId/$userId/${event.target.value}`,
													params: {
														orgId: params.orgId,
														orgUserId: params.orgUserId,
														userId: userById.data?.userId ?? "",
													},
													search: {
														mode: search.mode,
													},
												})
											}
											options={(showEditChrome ? editModeTabs : tabs).map(
												(tab) => ({
													label: tab.label,
													value: tab.id,
												}),
											)}
											className="mx-6 mt-3 w-80 md:hidden"
										/>
										{!hideTabs && (
											<div className="flex h-12 w-full shrink-0 flex-col items-stretch border-b border-secondary bg-primary px-(--spacing-3xl,24px) max-md:hidden">
												<Tabs
													selectedKey={currentTab}
													onSelectionChange={(event) =>
														navigate({
															to: `/org-admin/$orgId/users/$orgUserId/$userId/${event}`,
															params: {
																orgId: params.orgId,
																orgUserId: params.orgUserId,
																userId: userById.data?.userId ?? "",
															},
															search: {
																mode: search.mode,
															},
														})
													}
													className="h-full w-full"
												>
													<TabList
														type="underline"
														size="sm"
														items={showEditChrome ? editModeTabs : tabs}
														className="h-full w-full items-stretch gap-(--spacing-lg,12px) border-b-0"
													>
														{(tab) => (
															<DrawerTab
																id={tab.id}
																label={tab.label}
																className="h-full items-end -mb-px"
															/>
														)}
													</TabList>
												</Tabs>
											</div>
										)}
										<div className="flex w-full flex-col gap-(--spacing-xs,4px) p-(--spacing-xl,16px)">
											<TabContentWrapper tabKey={currentTab}>
												<Outlet />
											</TabContentWrapper>
										</div>
									</div>
								)}
							</Drawer.ScrollBody>
							{/* Drawer Footer - Renders based on state from child routes */}
							<DrawerFooterWithState
								currentTab={currentTab}
								searchMode={showEditChrome ? search.mode : "view"}
								params={{ ...params, userId: userById.data?.userId ?? "" }}
								hideTabs={hideTabs}
								editOrgAdminUserDetailsForm={editOrgAdminUserDetailsForm}
								close={close}
								handleClose={handleClose}
								navigate={navigate}
								t={t}
							/>
						</editOrgAdminUserDetailsForm.AppForm>
					</>
				)}
			</Drawer>
		</DrawerProvider>
	);
}

/**
 * Drawer Footer Component
 * Consumes state from child routes via DrawerContext and renders appropriate footer
 */
function DrawerFooterWithState({
	currentTab,
	searchMode,
	params,
	hideTabs: _hideTabs,
	editOrgAdminUserDetailsForm,
	close: _close,
	handleClose: _handleClose,
	navigate,
	t,
}: {
	currentTab: Tab;
	searchMode: Mode;
	params: { orgId: string; orgUserId: string; userId: string };
	hideTabs: boolean | string | undefined;
	editOrgAdminUserDetailsForm: any;
	close: () => void;
	handleClose: () => void;
	navigate: any;
	t: any;
}) {
	void _close;
	void _hideTabs;
	void _handleClose;
	// Get state from drawer context (pushed up from child routes)
	const _canSubmit = useDrawerStore((state) => state.canSubmit);
	const _isDirty = useDrawerStore((state) => state.isDirty);
	const _isSubmitting = useDrawerStore((state) => state.isSubmitting);
	const _handleSave = useDrawerStore((state) => state.handleSave);
	const _handleCancel = useDrawerStore((state) => state.handleCancel);
	void _canSubmit;
	void _isDirty;
	void _isSubmitting;
	void _handleSave;
	void _handleCancel;
	const clearErrors = useDrawerStore((state) => state.clearErrors);
	const clearSuccessMessages = useDrawerStore(
		(state) => state.clearSuccessMessages,
	);

	const location = useLocation();

	// biome-ignore lint/correctness/useExhaustiveDependencies: Zustand store functions are stable, and location/tab values are intentional triggers for clearing
	useEffect(() => {
		// Clear errors and success messages when route or viewed user changes.
		// DrawerFooterWithState is not nested under RouteComponent, so we must call
		// useLocation() here (not rely on window.location) and include user identity params.
		clearErrors();
		clearSuccessMessages();
	}, [
		location.pathname,
		location.search,
		currentTab,
		searchMode,
		params.orgUserId,
		params.userId,
	]);

	// Details tab in edit mode
	if (currentTab === "details" && searchMode === "edit") {
		return (
			<editOrgAdminUserDetailsForm.Subscribe
				selector={(s: any) => [s.canSubmit, s.isDirty, s.isSubmitting]}
			>
				{([formCanSubmit, formIsDirty, formIsSubmitting]: [
					boolean,
					boolean,
					boolean,
				]) => (
					<Drawer.Footer>
						<div className="flex flex-col gap-3 w-full">
							<div className="flex items-center justify-between gap-3 w-full">
								<div className="flex flex-col gap-2 flex-1 min-w-0 overflow-hidden">
									{editOrgAdminUserDetailsForm.successMessages.length > 0 && (
										<div className="flex flex-wrap gap-2">
											{editOrgAdminUserDetailsForm.successMessages.map(
												(_message: string) => (
													<BadgeGroup
														key={"success-${message}"}
														addonText="Success"
														theme="light"
														color="success"
														size="lg"
														showCloseButton={true}
														onClose={() =>
															editOrgAdminUserDetailsForm.clearSuccessMessages()
														}
														addonClassName="font-bold text-xs capitalize tracking-wide"
													>
														{"User updated"}
													</BadgeGroup>
												),
											)}
										</div>
									)}
									{editOrgAdminUserDetailsForm.errors.length > 0 && (
										<div className="flex flex-wrap gap-2">
											{editOrgAdminUserDetailsForm.errors.map(
												(_error: string) => (
													<BadgeGroup
														key={"error-${error}"}
														addonText="Error"
														theme="light"
														color="error"
														size="lg"
														showCloseButton={true}
														onClose={() =>
															editOrgAdminUserDetailsForm.clearErrors()
														}
														addonClassName="font-bold text-xs capitalize tracking-wide"
													>
														{"Failed to update user"}
													</BadgeGroup>
												),
											)}
										</div>
									)}
								</div>

								{/* Right side - Action buttons */}
								<div className="flex items-center gap-3">
									<Button
										color="secondary"
										size="md"
										onClick={() => {
											navigate({
												to: "/org-admin/$orgId/users/$orgUserId/$userId/details",
												params: {
													orgId: params.orgId,
													orgUserId: params.orgUserId,
													userId: params.userId,
												},
												search: { mode: "view" },
											});
											editOrgAdminUserDetailsForm.reset();
										}}
									>
										{t("common.actions.cancelEdits")}
									</Button>
									<Button
										color="primary"
										size="md"
										data-testid="org-user-details-save-btn"
										isLoading={formIsSubmitting}
										isDisabled={!formCanSubmit || !formIsDirty}
										onClick={() => {
											if (formCanSubmit && formIsDirty) {
												editOrgAdminUserDetailsForm.handleSubmit();
											}
										}}
									>
										{t("common.actions.saveEdits")}
									</Button>
								</div>
							</div>
						</div>
					</Drawer.Footer>
				)}
			</editOrgAdminUserDetailsForm.Subscribe>
		);
	}

	// Sub-Organizations tab in edit mode - footer is handled by the child component
	if (currentTab === "sub-organizations" && searchMode === "edit") {
		return null;
	}

	// Figma user drawer has no view-mode footer. Close stays in the header.
	return null;
}
